import React, { useState, useEffect, useCallback } from 'react';
import { Search, X, Phone, Calendar, CreditCard, MapPin, Globe, ExternalLink, Scissors, Coffee, Store, Sparkles, Car,
  ShoppingBag, Home, Camera, Wrench, Utensils, Heart, ChevronLeft, ChevronRight } from 'lucide-react';
import Encaisser from './Encaisser';
import SiretLookup from './SiretLookup';

/* « ouvert maintenant », repris tel quel de l'ancien ecran (horaires Google ou OSM) */
// Jours FR et OSM
const joursFR = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];
const joursOSM = { 'Mo': 1, 'Tu': 2, 'We': 3, 'Th': 4, 'Fr': 5, 'Sa': 6, 'Su': 0 };

function isOpenNow(horaires) {
  if (!horaires || horaires.trim() === '') return null; // inconnu

  const now = new Date();
  const jourActuel = now.getDay(); // 0=dimanche
  const heureActuelle = now.getHours() * 60 + now.getMinutes(); // minutes depuis minuit
  const jourNomFR = joursFR[jourActuel].toLowerCase();

  // Format Google: "lundi: 09:30–19:00 | mardi: ..."
  if (horaires.includes('|') && horaires.toLowerCase().includes(jourNomFR)) {
    const parts = horaires.split('|').map(s => s.trim());
    for (const part of parts) {
      const lower = part.toLowerCase();
      if (!lower.startsWith(jourNomFR)) continue;
      // Fermé ?
      if (lower.includes('ferm')) return false;
      // Extraire les heures (format: "09:30–19:00" ou "09:30 – 19:00" avec tiret unicode)
      const times = part.match(/(\d{1,2}:\d{2})[^\d]*(\d{1,2}:\d{2})/);
      if (times) {
        const [, debut, fin] = times;
        const [dh, dm] = debut.split(':').map(Number);
        const [fh, fm] = fin.split(':').map(Number);
        const open = dh * 60 + dm;
        const close = fh * 60 + fm;
        if (heureActuelle >= open && heureActuelle < close) return true;
        return false;
      }
      return null;
    }
    return null;
  }

  // Format OSM: "Tu-Sa 10:00-18:00; Su,Mo off"
  if (horaires.match(/[A-Z][a-z]/)) {
    const segments = horaires.split(';').map(s => s.trim());
    let matchedOpen = null;

    for (const seg of segments) {
      // Vérifier si ce jour est inclus dans le segment
      const isOff = seg.toLowerCase().includes('off');

      // Parser les jours du segment
      const daysPart = seg.split(/\s+/)[0];
      const days = [];

      daysPart.split(',').forEach(part => {
        if (part.includes('-')) {
          const [start, end] = part.split('-');
          const si = joursOSM[start];
          const ei = joursOSM[end];
          if (si !== undefined && ei !== undefined) {
            let d = si;
            while (true) {
              days.push(d);
              if (d === ei) break;
              d = (d + 1) % 7;
            }
          }
        } else if (joursOSM[part] !== undefined) {
          days.push(joursOSM[part]);
        }
      });

      if (!days.includes(jourActuel)) continue;

      if (isOff) return false;

      // Extraire les heures
      const times = seg.match(/(\d{1,2}:\d{2})-(\d{1,2}:\d{2})/);
      if (times) {
        const [, debut, fin] = times;
        const [dh, dm] = debut.split(':').map(Number);
        const [fh, fm] = fin.split(':').map(Number);
        const open = dh * 60 + dm;
        const close = fh * 60 + fm;
        if (heureActuelle >= open && heureActuelle < close) matchedOpen = true;
        else matchedOpen = false;
      }
    }

    return matchedOpen;
  }

  return null; // format non reconnu
}



/* ---------------------------------------------------------------------------
   Recherche Pro v2 (David, 08/10/2026) : les commerces trouves sur Google,
   en cartes, classes par etape de vente. Une grande recherche, des filtres
   qui disent combien ils montrent, une phrase qui dit ce qui est affiche,
   et sur chaque carte : Appeler, RDV, Encaisser (pay.e-application.fr).
   Les outils Google (budget, remplissage auto) sont dans l'onglet
   « Configuration API Google », qui garde l'ancien ecran.
   --------------------------------------------------------------------------- */
const API = process.env.REACT_APP_API_URL || '';
const entete = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' });
const post = async (u, b) => { const r = await fetch(`${API}${u}`, { method: 'POST', headers: entete(), body: JSON.stringify(b || {}) }); const j = await r.json().catch(() => ({})); if (!r.ok) throw new Error(j.error || `Erreur ${r.status}`); return j; };
const PAR_PAGE = 24;

const ETAPES = {
  nouveau:         { t: 'À appeler', n: 1, c: '#5b6380', fond: '#f1f3f8' },
  nrp:             { t: 'Ne répond pas', n: 2, c: '#b45309', fond: '#fff4e0' },
  a_rappeler:      { t: 'À rappeler', n: 2, c: '#b45309', fond: '#fff4e0' },
  interesse:       { t: 'Intéressé', n: 3, c: '#14448f', fond: '#e8f1ff' },
  rdv_pris:        { t: 'RDV pris', n: 3, c: '#14448f', fond: '#e8f1ff' },
  lien_envoye:     { t: 'Lien de paiement envoyé', n: 4, c: '#6b21a8', fond: '#f3e8ff' },
  client:          { t: 'Client', n: 5, c: '#1b5e20', fond: '#e3f6e8' },
  pas_interesse:   { t: 'Pas intéressé', n: 0, c: '#8a91a5', fond: '#f1f1f1' },
  ne_pas_rappeler: { t: 'Ne pas rappeler', n: 0, c: '#8a91a5', fond: '#f1f1f1' }
};
const ORDRE_ETAPES = ['nouveau', 'a_rappeler', 'nrp', 'interesse', 'rdv_pris', 'lien_envoye', 'client', 'pas_interesse', 'ne_pas_rappeler'];
const CATEGORIES = ['Restaurant', 'Pizzeria', 'Kebab', 'Traiteur', 'Cafe', 'Bar', 'Boulangerie', 'Patisserie', 'Chocolatier', 'Fromagerie', 'Poissonnier',
  'Salon de coiffure', 'Institut de beaute', 'Epicerie fine', 'Supermarche', 'Boucherie', 'Fleuriste', 'Pharmacie', 'Opticien', 'Dentiste', 'Medecin', 'Veterinaire',
  'Boutique vetements', 'Bijouterie', 'Agence immobiliere', 'Banque', 'Assurance', 'Garage auto', 'Caviste', 'Pressing', 'Librairie', 'Tabac Presse', 'Hotel',
  'Magasin informatique', 'Plombier Electricien', 'Serrurier', 'Peintre', 'Photographe', 'Tatoueur', 'Auto-ecole', 'Salle de sport'];
const VILLES = ['Paris', '92 - Hauts-de-Seine', '93 - Seine-Saint-Denis', '94 - Val-de-Marne', '91 - Essonne', '77 - Seine-et-Marne', '78 - Yvelines',
  "95 - Val-d'Oise", 'Marseille', 'Lyon', 'Toulouse', 'Nice', 'Nantes', 'Strasbourg', 'Montpellier', 'Bordeaux', 'Lille', 'Rennes'];

/* une couleur et un pictogramme par metier, pour reconnaitre une carte d'un coup d'oeil */
const metier = (a) => {
  const t = String(a || '').toLowerCase();
  const m = [[/coiff|barb/, Scissors, ['#f1d3c9', '#b5634f']], [/beaut|institut|onglerie|tatou/, Sparkles, ['#f3d9ea', '#a8457f']],
    [/restau|pizz|kebab|traiteur/, Utensils, ['#fde3c8', '#c46b2d']], [/caf|bar\b|bar$/, Coffee, ['#e8d9c9', '#7a5232']],
    [/boulang|patiss|chocol|from|poisson|bouch|epicer|superm|cavist/, ShoppingBag, ['#f6e7c1', '#a67a19']],
    [/garage|auto/, Car, ['#d9e2ef', '#43608a']], [/immob|hotel/, Home, ['#d8ece6', '#2f7a63']], [/photo/, Camera, ['#e1dcf4', '#5b4aa8']],
    [/plomb|serrur|peintr|informat/, Wrench, ['#dfe4f3', '#4a5a8f']], [/pharma|dent|medec|veter|optic/, Heart, ['#d7eef3', '#2b7b8f']]];
  const r = m.find(([re]) => re.test(t));
  return r ? { Ic: r[1], g: r[2] } : { Ic: Store, g: ['#dfe4f3', '#6b78b8'] };
};
const champ = { padding: '10px 12px', border: '1.5px solid #dfe3f2', borderRadius: 10, fontSize: 14, background: '#fff', boxSizing: 'border-box' };
const bouton = (fond, coul = '#fff') => ({ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '10px 14px', border: 'none',
  borderRadius: 11, background: fond, color: coul, fontWeight: 800, fontSize: 13, cursor: 'pointer', textDecoration: 'none', whiteSpace: 'nowrap' });
const dateFr = (d) => (d ? new Date(d).toLocaleDateString('fr-FR') : '');

function Pastille({ actif, onClick, children, n, c = '#000091', desactive }) {
  return (
    <button onClick={onClick} disabled={desactive} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 12px', borderRadius: 99,
      cursor: desactive ? 'default' : 'pointer', fontSize: 13, fontWeight: 700, opacity: desactive ? 0.45 : 1,
      border: `1.5px solid ${actif ? c : '#e3e7ef'}`, background: actif ? c : '#fff', color: actif ? '#fff' : '#3b3e5c' }}>
      {children}{n !== undefined && <span style={{ fontSize: 11.5, fontWeight: 800, padding: '1px 7px', borderRadius: 99, background: actif ? 'rgba(255,255,255,.25)' : '#f1f3f8', color: actif ? '#fff' : '#7a8194' }}>{Number(n).toLocaleString('fr-FR')}</span>}
    </button>);
}

function Carte({ r, onOuvrir, onRdv }) {
  const e = ETAPES[r.statut || 'nouveau'] || ETAPES.nouveau;
  const { Ic, g } = metier(r.activite);
  return (
    <article onClick={onOuvrir} style={{ background: '#fff', borderRadius: 18, border: '1px solid #e9ecf4', overflow: 'hidden', cursor: 'pointer',
      boxShadow: '0 18px 40px -32px rgba(20,20,80,.6)', display: 'flex', flexDirection: 'column' }}>
      <div style={{ height: 96, position: 'relative', background: `linear-gradient(135deg, ${g[0]}, ${g[1]})`, display: 'grid', placeItems: 'center' }}>
        <Ic size={38} color="rgba(255,255,255,.9)" />
        <span style={{ position: 'absolute', left: 10, top: 10, padding: '4px 10px', borderRadius: 99, fontSize: 11, fontWeight: 800, background: '#fff', color: e.c }}>{e.t}</span>
        <span style={{ position: 'absolute', right: 10, top: 10, padding: '4px 9px', borderRadius: 99, fontSize: 11, fontWeight: 800,
          background: r.site_web ? 'rgba(0,0,0,.55)' : '#e1000f', color: '#fff' }}>{r.site_web ? 'a un site' : 'sans site'}</span>
        {(() => { const o = isOpenNow(r.horaires); return o === null ? null : (
          <span style={{ position: 'absolute', left: 10, bottom: 8, padding: '3px 9px', borderRadius: 99, fontSize: 11, fontWeight: 800,
            background: o ? '#18a34a' : 'rgba(0,0,0,.55)', color: '#fff' }}>{o ? 'Ouvert maintenant' : 'Fermé'}</span>); })()}
      </div>
      <div style={{ padding: '12px 14px', flex: 1, display: 'flex', flexDirection: 'column' }}>
        <div style={{ fontWeight: 800, fontSize: 15.5, color: '#14143a', lineHeight: 1.25 }}>{r.nom}</div>
        <div style={{ fontSize: 12, color: '#7a8194', marginTop: 3 }}>{r.activite}{r.ville ? ` · ${r.ville}` : ''}{r.code_postal ? ` ${r.code_postal}` : ''}</div>
        <div style={{ display: 'flex', gap: 4, margin: '10px 0 6px' }}>
          {[1, 2, 3, 4, 5].map((n) => <i key={n} style={{ flex: 1, height: 6, borderRadius: 9, background: e.n === 0 ? '#e8ebf3' : n <= e.n ? (e.n === 5 ? '#18a34a' : '#000091') : '#e8ebf3' }} />)}
        </div>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#3b3e5c' }}>{e.n ? `Étape ${e.n}/5 : ${e.t.toLowerCase()}` : e.t}
          {r.statut_par_nom ? <span style={{ fontWeight: 500, color: '#9aa3b8' }}> · {r.statut_par_nom}{r.statut_date ? `, ${dateFr(r.statut_date)}` : ''}</span> : null}</div>
        <div style={{ display: 'flex', gap: 6, marginTop: 'auto', paddingTop: 10 }}>
          <a href={`tel:${r.telephone}`} onClick={(ev) => ev.stopPropagation()} style={{ ...bouton('#eef1f8', '#000091'), flex: 1, padding: '8px 6px' }}><Phone size={13} /> Appeler</a>
          <button onClick={(ev) => { ev.stopPropagation(); onRdv(r); }} style={{ ...bouton('#eef1f8', '#000091'), flex: 1, padding: '8px 6px' }}><Calendar size={13} /> RDV</button>
          <button onClick={(ev) => { ev.stopPropagation(); onOuvrir('encaisser'); }} style={{ ...bouton(r.statut === 'client' ? '#e3f6e8' : '#18a34a', r.statut === 'client' ? '#1b5e20' : '#fff'), flex: 1, padding: '8px 6px' }}>
            <CreditCard size={13} /> {r.statut === 'client' ? 'Suivi' : 'Encaisser'}</button>
        </div>
      </div>
    </article>);
}

export default function RecherchePro({ currentUser }) {
  const [q, setQ] = useState('');
  const [qApplique, setQApplique] = useState('');
  const [activite, setActivite] = useState('');
  const [ville, setVille] = useState('');
  const [statut, setStatut] = useState('');
  const [sansSite, setSansSite] = useState(false);
  const [ouvert, setOuvert] = useState(false);
  const [exportEnCours, setExportEnCours] = useState(false);
  const estAdmin = currentUser && currentUser.role === 'admin';
  const [page, setPage] = useState(1);
  const [res, setRes] = useState({ results: [], total: 0, totalPages: 1 });
  const [cpt, setCpt] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [fiche, setFiche] = useState(null);           /* { r, encaisser } */
  const [rdv, setRdv] = useState(null);
  const [commerciaux, setCommerciaux] = useState([]);
  const [google, setGoogle] = useState(false);
  const [gForm, setGForm] = useState({ activite: '', ville: 'Paris', arrondissement: '' });
  const [gMsg, setGMsg] = useState(null);

  /* la recherche part 400 ms apres la derniere touche */
  useEffect(() => { const t = setTimeout(() => { setQApplique(q.trim()); setPage(1); }, 400); return () => clearTimeout(t); }, [q]);
  const charger = useCallback(async () => {
    setChargement(true); setErreur(null);
    const filtres = { activite: activite || undefined, ville: ville || undefined, search: qApplique || undefined, sans_site: sansSite || undefined };
    try {
      const [r, c] = await Promise.all([
        post('/api/fichier-pro/base', { ...filtres, statut: statut || undefined, page, limit: PAR_PAGE }),
        post('/api/fichier-pro/compteurs', filtres)]);
      setRes({ results: r.results || [], total: r.total || 0, totalPages: r.totalPages || 1 }); setCpt(c);
    } catch (e) { setErreur(e.message); }
    setChargement(false);
  }, [activite, ville, qApplique, sansSite, statut, page]);
  useEffect(() => { charger(); }, [charger]);
  useEffect(() => {
    fetch(`${API}/api/planning/commerciaux`, { headers: entete() }).then((r) => r.json())
      .then((j) => setCommerciaux(Array.isArray(j) ? j : (j.commerciaux || j.data || []))).catch(() => null);
  }, []);

  const nbStatut = (k) => { const x = cpt && cpt.parStatut.find((s) => s.statut === k); return x ? x.n : 0; };
  const totalTous = cpt ? cpt.parStatut.reduce((n, s) => n + s.n, 0) : 0;
  const metiers = cpt ? cpt.parActivite.slice(0, 9) : [];
  const toutEffacer = () => { setQ(''); setActivite(''); setVille(''); setStatut(''); setSansSite(false); setOuvert(false); setPage(1); };
  const affiches = ouvert ? res.results.filter((r) => isOpenNow(r.horaires) === true) : res.results;
  const exporter = async () => {
    setExportEnCours(true);
    try {
      const p = new URLSearchParams(); if (activite) p.append('activite', activite); if (ville) p.append('ville', ville); if (qApplique) p.append('search', qApplique); if (statut) p.append('statut', statut);
      const r = await fetch(`${API}/api/fichier-pro/export-csv?${p.toString()}`, { headers: entete() });
      if (!r.ok) throw new Error('export impossible');
      const url = URL.createObjectURL(await r.blob()); const a = document.createElement('a'); a.href = url; a.download = `eshops-base-${new Date().toISOString().slice(0, 10)}.csv`; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { alert(e.message); }
    setExportEnCours(false);
  };
  const filtreActif = q || activite || ville || statut || sansSite || ouvert;
  const phrase = `${Number(res.total).toLocaleString('fr-FR')} commerce${res.total > 1 ? 's' : ''}`
    + `${activite ? ` · ${activite}` : ''}${ville ? ` · ${ville}` : ''}${statut ? ` · ${(ETAPES[statut] || {}).t}` : ''}${sansSite ? ' · sans site internet' : ''}${ouvert ? ' · ouverts maintenant sur cette page' : ''}${qApplique ? ` · recherche « ${qApplique} »` : ''}`;

  const changerStatut = async (r, s) => {
    try {
      await post('/api/fichier-pro/statut', { telephone_clean: r.telephone_clean, statut: s });
      setFiche((f) => (f && f.r.id === r.id ? { ...f, r: { ...f.r, statut: s, statut_par_nom: currentUser && currentUser.nom } } : f));
      charger();
    } catch (e) { alert(e.message); }
  };
  const ouvrirRdv = (r) => setRdv({ r, commercial_id: (commerciaux[0] || {}).id || '', date_rdv: '', heure_debut: '10:00', description: '' });
  const enregistrerRdv = async () => {
    const r = rdv.r;
    const [h, m] = rdv.heure_debut.split(':').map(Number);
    try {
      await post('/api/planning/rdv', { commercial_id: rdv.commercial_id, titre: `RDV ${r.activite || ''} - ${r.nom}`, description: rdv.description,
        client_nom: r.nom, client_telephone: r.telephone, client_adresse: r.adresse, date_rdv: rdv.date_rdv, heure_debut: rdv.heure_debut,
        heure_fin: `${String(h + 1).padStart(2, '0')}:${String(m).padStart(2, '0')}`, statut: 'planifie' });
      await post('/api/fichier-pro/statut', { telephone_clean: r.telephone_clean, statut: 'rdv_pris' }).catch(() => null);
      setRdv(null); charger();
    } catch (e) { alert(e.message); }
  };
  const lancerGoogle = async () => {
    if (!gForm.activite || !gForm.ville) { setGMsg({ ko: true, t: 'Choisissez un métier et une ville.' }); return; }
    setGMsg({ t: 'Recherche sur Google en cours (jusqu’à 2 minutes)…' });
    try {
      const j = await post('/api/fichier-pro/search', { activite: gForm.activite, ville: gForm.ville, arrondissement: gForm.arrondissement || undefined });
      setGMsg({ t: `${j.total || (j.results || []).length} commerces trouvés et ajoutés à la base.` });
      setActivite(gForm.activite); setVille(gForm.ville); setStatut(''); setPage(1);
    } catch (e) { setGMsg({ ko: true, t: e.message }); }
  };

  return (
    <div style={{ background: '#fff', borderRadius: 16, padding: '20px 22px 28px', boxShadow: '0 4px 18px rgba(0,0,0,.08)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ flex: 1 }}>
          <h2 style={{ margin: 0, fontSize: 21, color: '#14143a', display: 'flex', alignItems: 'center', gap: 8 }}><Search size={20} color="#000091" /> Recherche Pro</h2>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: '#5b6380' }}>Les commerces trouvés sur Google, classés par étape. Une carte = un commerce : où il en est, et les actions en un clic.</p>
        </div>
        {estAdmin && <button onClick={exporter} disabled={exportEnCours} style={bouton('#e7f8ec', '#1b5e20')}>{exportEnCours ? 'Export…' : 'Exporter CSV'}</button>}
        {estAdmin && <button onClick={() => setGoogle(!google)} style={bouton(google ? '#000091' : '#eef1f8', google ? '#fff' : '#000091')}><Search size={14} /> Chercher de nouveaux commerces sur Google</button>}
      </div>

      {google && (
        <div style={{ marginTop: 14, padding: 14, borderRadius: 14, background: '#f2f4ff', border: '1px solid #d9dcf2' }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <select style={champ} value={gForm.activite} onChange={(e) => setGForm({ ...gForm, activite: e.target.value })}>
              <option value="">Métier…</option>{CATEGORIES.map((c) => <option key={c}>{c}</option>)}</select>
            <select style={champ} value={gForm.ville} onChange={(e) => setGForm({ ...gForm, ville: e.target.value, arrondissement: '' })}>
              {VILLES.map((v) => <option key={v}>{v}</option>)}</select>
            {gForm.ville === 'Paris' && <select style={champ} value={gForm.arrondissement} onChange={(e) => setGForm({ ...gForm, arrondissement: e.target.value })}>
              <option value="">Tout Paris</option>{Array.from({ length: 20 }, (_, i) => <option key={i} value={`750${String(i + 1).padStart(2, '0')}`}>{i + 1}{i ? 'e' : 'er'} arrondissement</option>)}</select>}
            <button onClick={lancerGoogle} style={bouton('#000091')}>Lancer la recherche</button>
            <span style={{ fontSize: 12, color: '#5b6380' }}>Chaque recherche consomme des appels Google payants. Budget et remplissage automatique : onglet « Configuration API Google ».</span>
          </div>
          {gMsg && <div style={{ marginTop: 8, fontSize: 13, fontWeight: 700, color: gMsg.ko ? '#b71c1c' : '#1b5e20' }}>{gMsg.t}</div>}
        </div>)}

      <div style={{ position: 'relative', marginTop: 14 }}>
        <Search size={19} style={{ position: 'absolute', left: 16, top: 15, color: '#7a8194' }} />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un commerce : nom, rue, ville, code postal, téléphone, site…"
          style={{ ...champ, width: '100%', padding: '14px 44px 14px 46px', fontSize: 15.5, borderRadius: 14, border: '2px solid #d9dcf2', background: '#fbfcff' }} />
        {q && <button onClick={() => setQ('')} style={{ position: 'absolute', right: 10, top: 10, width: 30, height: 30, borderRadius: 99, border: 'none', background: '#eef1f8', cursor: 'pointer' }}><X size={15} /></button>}
      </div>

      <div style={{ display: 'grid', gap: 10, marginTop: 14, padding: '14px 16px', borderRadius: 14, background: '#f7f8fc' }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ width: 84, fontSize: 12, fontWeight: 800, color: '#7a8194', textTransform: 'uppercase' }}>Métier</span>
          <Pastille actif={!activite} onClick={() => { setActivite(''); setPage(1); }}>Tous</Pastille>
          {metiers.map((m) => <Pastille key={m.activite} actif={activite === m.activite} n={m.n} onClick={() => { setActivite(activite === m.activite ? '' : m.activite); setPage(1); }}>{m.activite}</Pastille>)}
          {cpt && cpt.parActivite.length > 9 && (
            <select style={{ ...champ, padding: '6px 10px', fontSize: 13, width: 'auto', maxWidth: 260 }} value={metiers.some((m) => m.activite === activite) ? '' : activite} onChange={(e) => { setActivite(e.target.value); setPage(1); }}>
              <option value="">Autres métiers…</option>{cpt.parActivite.slice(9).map((m) => <option key={m.activite} value={m.activite}>{m.activite} ({m.n})</option>)}</select>)}
          <select style={{ ...champ, padding: '6px 10px', fontSize: 13, marginLeft: 'auto', width: 'auto', maxWidth: 260 }} value={ville} onChange={(e) => { setVille(e.target.value); setPage(1); }}>
            <option value="">Toutes les villes</option>{(cpt ? cpt.villes : []).map((v) => <option key={v.ville} value={v.ville}>{v.ville} ({v.n})</option>)}</select>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ width: 84, fontSize: 12, fontWeight: 800, color: '#7a8194', textTransform: 'uppercase' }}>Étape</span>
          <Pastille actif={!statut} n={totalTous} onClick={() => { setStatut(''); setPage(1); }}>Toutes</Pastille>
          {ORDRE_ETAPES.map((k) => <Pastille key={k} actif={statut === k} n={nbStatut(k)} c={ETAPES[k].c} desactive={!nbStatut(k) && statut !== k}
            onClick={() => { setStatut(statut === k ? '' : k); setPage(1); }}>{ETAPES[k].t}</Pastille>)}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ width: 84, fontSize: 12, fontWeight: 800, color: '#b3000c', textTransform: 'uppercase' }}>Il manque</span>
          <Pastille actif={sansSite} c="#b3000c" n={cpt ? cpt.sansSite : undefined} onClick={() => { setSansSite(!sansSite); setPage(1); }}>Sans site internet</Pastille>
          <span style={{ width: 1, height: 22, background: '#dfe3ea', margin: '0 6px' }} />
          <Pastille actif={ouvert} c="#18a34a" onClick={() => setOuvert(!ouvert)}>Ouvert maintenant (sur cette page)</Pastille>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', margin: '14px 2px 10px' }}>
        <b style={{ fontSize: 15, color: '#14143a' }}>{phrase}</b>
        {filtreActif && <button onClick={toutEffacer} style={{ ...bouton('#fff', '#000091'), border: '1px solid #d9dcf2', padding: '6px 12px' }}><X size={13} /> Tout réafficher</button>}
      </div>
      {erreur && <div style={{ background: '#ffebee', color: '#b71c1c', padding: '12px 16px', borderRadius: 8 }}>{erreur}</div>}
      {chargement && !res.results.length ? <p style={{ color: '#90a4ae' }}>Chargement…</p> : !res.results.length ? (
        <div style={{ padding: 26, borderRadius: 16, background: '#f7f8fc', textAlign: 'center', color: '#5b6380' }}>Aucun commerce ne correspond.
          {filtreActif && <button onClick={toutEffacer} style={{ ...bouton('#000091'), marginLeft: 8 }}>Tout réafficher</button>}</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16, opacity: chargement ? 0.6 : 1 }}>
          {affiches.map((r) => <Carte key={r.id || r.telephone_clean} r={r} onOuvrir={(m) => setFiche({ r, encaisser: m === 'encaisser' })} onRdv={ouvrirRdv} />)}
        </div>)}
      {res.totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 10, marginTop: 18 }}>
          <button disabled={page <= 1} onClick={() => setPage(page - 1)} style={{ ...bouton('#eef1f8', '#000091'), opacity: page <= 1 ? 0.4 : 1 }}><ChevronLeft size={15} /> Précédent</button>
          <b style={{ fontSize: 13.5 }}>Page {page} / {res.totalPages}</b>
          <button disabled={page >= res.totalPages} onClick={() => setPage(page + 1)} style={{ ...bouton('#eef1f8', '#000091'), opacity: page >= res.totalPages ? 0.4 : 1 }}>Suivant <ChevronRight size={15} /></button>
        </div>)}

      {/* fiche du commerce */}
      {fiche && (() => {
        const r = fiche.r, e = ETAPES[r.statut || 'nouveau'] || ETAPES.nouveau, { Ic, g } = metier(r.activite);
        return (
          <div onClick={(ev) => ev.target === ev.currentTarget && setFiche(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(10,15,30,.55)', zIndex: 1000, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', overflowY: 'auto', padding: '28px 14px' }}>
            <div style={{ background: '#fff', borderRadius: 18, width: 'min(1060px, 100%)', boxShadow: '0 30px 70px rgba(0,0,0,.3)', overflow: 'hidden' }}>
              <div style={{ height: 110, background: `linear-gradient(135deg, ${g[0]}, ${g[1]})`, position: 'relative', display: 'flex', alignItems: 'flex-end', padding: '0 22px 14px', gap: 14 }}>
                <Ic size={42} color="rgba(255,255,255,.9)" />
                <div style={{ color: '#fff' }}><div style={{ fontSize: 22, fontWeight: 900 }}>{r.nom}</div><div style={{ fontSize: 13, opacity: 0.9 }}>{r.activite}{r.ville ? ` · ${r.ville}` : ''}</div></div>
                <button onClick={() => setFiche(null)} style={{ position: 'absolute', right: 14, top: 12, width: 34, height: 34, borderRadius: 99, border: 'none', background: '#fff', cursor: 'pointer' }}><X size={17} /></button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 18, padding: 20 }}>
                <div>
                  {[['Téléphone', <a href={`tel:${r.telephone}`} style={{ color: '#000091', fontWeight: 700 }}>{r.telephone}</a>],
                    ['Adresse', r.adresse ? <a href={`https://www.google.com/maps/search/${encodeURIComponent(`${r.nom} ${r.adresse}`)}`} target="_blank" rel="noopener noreferrer" style={{ color: '#000091' }}><MapPin size={12} /> {r.adresse}</a> : '—'],
                    ['Site internet', r.site_web ? <a href={r.site_web.startsWith('http') ? r.site_web : `https://${r.site_web}`} target="_blank" rel="noopener noreferrer" style={{ color: '#000091' }}><Globe size={12} /> {r.site_web}</a> : <b style={{ color: '#b3000c' }}>aucun : à proposer</b>],
                    ['Horaires', r.horaires || '—'], ['Code postal', r.code_postal || '—'], ['Ajouté le', dateFr(r.date_ajout)],
                    ['Suivi par', r.statut_par_nom ? `${r.statut_par_nom}${r.statut_date ? `, le ${dateFr(r.statut_date)}` : ''}` : 'personne']]
                    .map(([t, v]) => <div key={t} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 13.5, padding: '7px 0', borderBottom: '1px dashed #edf0f6' }}><span style={{ color: '#7a8194' }}>{t}</span><span style={{ textAlign: 'right' }}>{v}</span></div>)}
                  <div style={{ marginTop: 14 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 800, color: '#5b6380', marginBottom: 6 }}>Étape</div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {ORDRE_ETAPES.map((k) => <button key={k} onClick={() => changerStatut(r, k)} style={{ padding: '6px 10px', borderRadius: 99, cursor: 'pointer', fontSize: 12, fontWeight: 800,
                        border: `1.5px solid ${(r.statut || 'nouveau') === k ? ETAPES[k].c : '#e3e7ef'}`, background: (r.statut || 'nouveau') === k ? ETAPES[k].fond : '#fff', color: ETAPES[k].c }}>{ETAPES[k].t}</button>)}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
                    <a href={`tel:${r.telephone}`} style={bouton('#000091')}><Phone size={14} /> Appeler</a>
                    <button onClick={() => { setFiche(null); ouvrirRdv(r); }} style={bouton('#eef1f8', '#000091')}><Calendar size={14} /> Prendre RDV</button>
                    {r.adresse && <a href={`https://www.google.com/maps/search/${encodeURIComponent(`${r.nom} ${r.adresse}`)}`} target="_blank" rel="noopener noreferrer" style={bouton('#fff', '#5b6380')}><ExternalLink size={14} /> Voir sur Google Maps</a>}
                  </div>
                  <div style={{ marginTop: 10, fontSize: 12, color: e.c, fontWeight: 700 }}>{e.n ? `Étape ${e.n}/5 : ${e.t}` : e.t}</div>
                </div>
                <div>{r.id ? <Encaisser source="recherche_pro" ficheId={r.id} client={{ nom: r.nom, entreprise: r.nom, telephone: r.telephone, email: r.email }} />
                  : <p style={{ color: '#7a8194' }}>Ce commerce n’est pas encore enregistré dans la base.</p>}</div>
              </div>
            </div>
          </div>);
      })()}

      {/* prise de RDV */}
      {rdv && (
        <div onClick={(ev) => ev.target === ev.currentTarget && setRdv(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(10,15,30,.55)', zIndex: 1001, display: 'grid', placeItems: 'center', padding: 14 }}>
          <div style={{ background: '#fff', borderRadius: 18, width: 'min(460px, 100%)', padding: 20 }}>
            <b style={{ fontSize: 17 }}>Prendre RDV : {rdv.r.nom}</b>
            <div style={{ display: 'grid', gap: 10, marginTop: 12 }}>
              <label style={{ fontSize: 12.5, color: '#5b6380' }}>Commercial<select style={{ ...champ, width: '100%' }} value={rdv.commercial_id} onChange={(e) => setRdv({ ...rdv, commercial_id: e.target.value })}>
                {commerciaux.map((c) => <option key={c.id} value={c.id}>{c.nom}</option>)}</select></label>
              <div style={{ display: 'flex', gap: 8 }}>
                <label style={{ fontSize: 12.5, color: '#5b6380', flex: 2 }}>Date<input type="date" style={{ ...champ, width: '100%' }} value={rdv.date_rdv} onChange={(e) => setRdv({ ...rdv, date_rdv: e.target.value })} /></label>
                <label style={{ fontSize: 12.5, color: '#5b6380', flex: 1 }}>Heure<input type="time" style={{ ...champ, width: '100%' }} value={rdv.heure_debut} onChange={(e) => setRdv({ ...rdv, heure_debut: e.target.value })} /></label>
              </div>
              <SiretLookup compact onCompanyFound={(d) => setRdv((x) => ({ ...x, description: `${x.description ? x.description + '\n' : ''}${d.nom_entreprise || d.denomination || ''}${d.siret ? ` · SIRET ${d.siret}` : ''}${d.dirigeant_nom ? ` · dirigeant ${d.dirigeant_nom}` : ''}${d.adresse ? ` · ${d.adresse}` : ''}` }))} />
              <label style={{ fontSize: 12.5, color: '#5b6380' }}>Note<textarea style={{ ...champ, width: '100%', minHeight: 60 }} value={rdv.description} onChange={(e) => setRdv({ ...rdv, description: e.target.value })} /></label>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 14, justifyContent: 'flex-end' }}>
              <button onClick={() => setRdv(null)} style={bouton('#fff', '#5b6380')}>Annuler</button>
              <button onClick={enregistrerRdv} disabled={!rdv.date_rdv || !rdv.commercial_id} style={{ ...bouton('#000091'), opacity: !rdv.date_rdv || !rdv.commercial_id ? 0.5 : 1 }}><Calendar size={14} /> Enregistrer le RDV</button>
            </div>
          </div>
        </div>)}
    </div>
  );
}
